import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { setSession } from 'src/services/authentication';
import { setAuth0Client } from 'src/utils/auth0-client';
import config from 'src/utils/config';
import routeNames from 'src/utils/routeNames';

interface AuthenticatedProps {
  children: ReactNode;
}

const Authenticated: FC<AuthenticatedProps> = (props: { children: any }) => {
  const { children } = props;
  const {
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    getAccessTokenSilently,
  } = useAuth0();

  // Expose Auth0 SDK functions to the axios interceptor on every render.
  // Must be synchronous (not in useEffect) so the refs are ready before
  // children render and fire their first API calls.
  setAuth0Client(getIdTokenClaims, getAccessTokenSilently, isAuthenticated);

  const setSessionIfAuthenticated = useCallback(async () => {
    if (isAuthenticated) {
      const idToken = await getIdTokenClaims();
      const accessToken = await getAccessTokenSilently({
        authorizationParams: { audience: config.audience },
      });
      setSession(idToken.__raw, accessToken);
    }
  }, [isAuthenticated, getIdTokenClaims, getAccessTokenSilently]);

  useEffect(() => {
    setSessionIfAuthenticated();
  }, [setSessionIfAuthenticated]);

  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>(
    null,
  );

  if (!isLoading) {
    if (!isAuthenticated) {
      if (location.pathname !== requestedLocation) {
        setRequestedLocation(location.pathname);
      }
      return (
        <Navigate
          to={`${routeNames.auth.login}?next=${encodeURI(requestedLocation)}`}
        />
      );
    }

    if (requestedLocation && location.pathname !== requestedLocation) {
      setRequestedLocation(null);
      return <Navigate to={requestedLocation} />;
    }

    return <>{children}</>;
  }

  // Show loading state instead of empty fragment
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '16px',
        color: '#666',
      }}
    >
      Loading authentication...
    </div>
  );
};

export default Authenticated;
